import { useCallback, useMemo } from 'react';
import { canLaunchLesson } from 'ContentAccess';
import { useCurrentUser } from 'FrontRoyalAngular';
import { type StreamForStreamDashboard, type LessonForStreamDashboard } from 'Lessons';
import { toLessonForDashboard, toStreamForDashboard } from '../toStreamForDashboard';

/*
    Caches canLaunchLesson per lesson. Each call rebuilds an Iguana stream from the whole payload, so a
    30-lesson course would pay that 30 times per render.
*/
export function useLessonAccess(stream: StreamForStreamDashboard) {
    const currentUser = useCurrentUser();

    const streamForAccess = useMemo(() => toStreamForDashboard(stream), [stream]);
    const cache = useMemo(() => new Map<string, ReturnType<typeof canLaunchLesson>>(), [streamForAccess, currentUser]);

    return useCallback(
        (lesson: LessonForStreamDashboard) => {
            const cached = cache.get(lesson.id);
            if (cached) return cached;

            const access = canLaunchLesson({
                lesson: toLessonForDashboard(stream, lesson),
                user: currentUser,
                stream: streamForAccess,
                lessonProgress: lesson.lessonProgress,
                streamProgress: stream.lessonStreamsProgress,
            });

            cache.set(lesson.id, access);
            return access;
        },
        [cache, stream, streamForAccess, currentUser],
    );
}
